import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { useTranslation } from 'react-i18next';
import { RefreshCcw } from 'lucide-react';
import { useGetContractDocumentEvents } from '../hooks/use-contract-document-events';
import { ContractEventsTimeline } from './contract-events-timeline';
import { cn } from '@/lib/utils';

interface ContractTimelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentTitle?: string;
}

const ContractTimelineDialog = ({
  open,
  onOpenChange,
  documentId,
  documentTitle,
}: ContractTimelineDialogProps) => {
  const { t } = useTranslation();
  const {
    data: eventsResult,
    isLoading,
    isFetching,
    refetch,
  } = useGetContractDocumentEvents(documentId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Mobile: ~95vw box capped to the dynamic viewport so the timeline
          never spills off-screen; desktop: original max-w-xl. The header
          reserves right padding so the Dialog's absolute-positioned auto
          close X never overlaps the title or any header control. */}
      <DialogContent className="bg-white w-[95vw] max-w-xl max-h-[90dvh] p-4 sm:p-6 rounded-2xl flex flex-col gap-4">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            {t('ORGCONTRACTS_TIMELINE_DIALOG_TITLE')}
          </DialogTitle>
          <DialogDescription className="line-clamp-1">
            {documentTitle ?? t('ORGCONTRACTS_TIMELINE_DIALOG_DESCRIPTION')}
          </DialogDescription>
        </DialogHeader>

        {/* Refresh toolbar — lives on its own row above the timeline so it
            never fights the auto-close button for space. Right-aligned and
            visually attached to the data it refreshes. */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t('ORGCONTRACTS_TIMELINE_REFRESH')}
            title={t('ORGCONTRACTS_TIMELINE_REFRESH')}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-medium-emphasis transition hover:bg-muted hover:text-high-emphasis disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCcw className={cn('size-3.5', isFetching && 'animate-spin')} />
            <span>{t('ORGCONTRACTS_TIMELINE_REFRESH')}</span>
          </button>
        </div>

        {/* Native overflow-y-auto instead of Radix ScrollArea — touch
            scroll inside a height-constrained dialog is unreliable with
            ScrollArea on mobile. Native scrolling honours the flex chain
            (`flex-1 min-h-0`) and works on every device. */}
        <div className="flex-1 min-h-0 overflow-y-auto -mr-3 pr-3">
          <ContractEventsTimeline events={eventsResult?.items ?? []} isLoading={isLoading} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContractTimelineDialog;
